import { useState } from "react";
import { Smartphone, Calendar, Zap, ChevronUp, X } from "lucide-react";
import { useLocation } from 'react-router';
import { useNavigate } from '../i18n/routing';
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import ScheduleDemo from "./ScheduleDemo";
import { tr } from '../i18n/text';

// Apple Icon
const AppleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

// Play Store Icon
const PlayIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
  </svg>
);

export default function FloatingActionBar() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const { t } = useLanguage();
  const isDark = resolvedTheme === "dark";

  // Hide on login / dashboard pages
  const shouldHide =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/dashboard" ||
    location.pathname.startsWith("/dashboard/");

  if (shouldHide) return null;

  const handleScheduleDemo = () => {
    setShowDemoModal(true);
    setIsExpanded(false);
  };

  const handleGetStarted = () => {
    window.location.href = "/free-trial";
    setIsExpanded(false);
  };

  return (
    <>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-3">

        {/* Expanded panel */}
        {isExpanded && (
          <div
            className="w-80 rounded-2xl overflow-hidden animate-slide-up"
            style={{
              backgroundColor: isDark ? "var(--bg-card)" : "var(--bg-card)",
              border: "1px solid var(--border-color)",
              boxShadow: isDark
                ? "var(--shadow-lg), var(--shadow-glow-indigo)"
                : "var(--shadow-lg)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
            }}
          >
            {/* Panel header */}
            <div
              className="flex items-center justify-between px-5 py-4"
              style={{ borderBottom: "1px solid var(--border-color)" }}
            >
              <div>
                <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                  {t("common.actions.getTrainZilla", { defaultValue: "Get TrainZilla" })}
                </p>
                <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                  {t("floatingBar.subtitle", { defaultValue: "Start your fitness revolution today" })}
                </p>
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                className="w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-150"
                style={{ backgroundColor: "var(--bg-tertiary)", color: "var(--text-muted)" }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.color = "var(--text-primary)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.color = "var(--text-muted)";
                }}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {/* Primary CTA */}
              <a
                href="/free-trial"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5"
                style={{
                  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                  boxShadow: "0 4px 14px rgba(99,102,241,0.4)",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow =
                    "0 6px 20px rgba(99,102,241,0.55)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow =
                    "0 4px 14px rgba(99,102,241,0.4)";
                }}
              >
                <Zap className="w-4 h-4" />
                {t("common.actions.startFreeTrial", { defaultValue: "Start Free Trial" })}
              </a>

              {/* Demo CTA */}
              <button
                onClick={handleScheduleDemo}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5"
                style={{
                  backgroundColor: "var(--bg-tertiary)",
                  border: "1px solid var(--border-color)",
                  color: "var(--text-primary)",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = "var(--accent-indigo)";
                  (e.currentTarget as HTMLElement).style.color = "var(--accent-indigo)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = "var(--border-color)";
                  (e.currentTarget as HTMLElement).style.color = "var(--text-primary)";
                }}
              >
                <Calendar className="w-4 h-4" />
                {t("common.actions.scheduleDemo", { defaultValue: "Schedule a Demo" })}
              </button>

              {/* Divider */}
              <div
                className="flex items-center gap-3 pt-1"
                style={{ borderTop: "1px solid var(--border-color)" }}
              >
                <span className="text-xs pt-3" style={{ color: "var(--text-muted)" }}>
                  {t("common.actions.downloadCoachApp", { defaultValue: "Coach App" })}
                </span>
              </div>

              {/* App store links */}
              <div className="grid grid-cols-2 gap-2">
                <a
                  href="https://play.google.com/store/apps/details?id=in.trainzilla.coach"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium transition-all duration-200 hover:-translate-y-0.5"
                  style={{
                    backgroundColor: "var(--bg-tertiary)",
                    border: "1px solid var(--border-color)",
                    color: "var(--text-secondary)",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = "var(--accent-indigo)";
                    (e.currentTarget as HTMLElement).style.color = "var(--text-primary)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = "var(--border-color)";
                    (e.currentTarget as HTMLElement).style.color = "var(--text-secondary)";
                  }}
                >
                  <PlayIcon className="w-4 h-4 flex-shrink-0" />
                  <span>{tr("Google Play")}</span>
                </a>
                <a
                  href="https://apps.apple.com/us/app/trainzilla-coach/id6760616851"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium transition-all duration-200 hover:-translate-y-0.5"
                  style={{
                    backgroundColor: "var(--bg-tertiary)",
                    border: "1px solid var(--border-color)",
                    color: "var(--text-secondary)",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = "var(--accent-indigo)";
                    (e.currentTarget as HTMLElement).style.color = "var(--text-primary)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = "var(--border-color)";
                    (e.currentTarget as HTMLElement).style.color = "var(--text-secondary)";
                  }}
                >
                  <AppleIcon className="w-4 h-4 flex-shrink-0" />
                  <span>{tr("App Store")}</span>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Trigger pill */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2.5 px-5 py-3 rounded-2xl text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:scale-105"
          style={{
            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
            boxShadow: isDark
              ? "0 8px 32px rgba(99,102,241,0.45), 0 2px 8px rgba(0,0,0,0.4)"
              : "0 8px 32px rgba(99,102,241,0.35), 0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Smartphone className="w-4 h-4" />
          <span>{t("common.actions.getTrainZilla", { defaultValue: "Get TrainZilla" })}</span>
          {/* Free badge */}
          <span
            className="text-xs px-2 py-0.5 rounded-full font-semibold"
            style={{
              backgroundColor: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          >
            {tr("Free")}
          </span>
          <ChevronUp
            className={`w-3.5 h-3.5 transition-transform duration-300 ${isExpanded ? "" : "rotate-180"}`}
          />
        </button>
      </div>

      {/* Demo Modal */}
      <ScheduleDemo
        isOpen={showDemoModal}
        onClose={() => setShowDemoModal(false)}
        onSuccess={() => setShowDemoModal(false)}
      />
    </>
  );
}